// src/pages/LandingPage.js
import React, { useEffect, useState, useRef, useContext } from 'react';
import { Box, CircularProgress, Typography, Avatar, Menu, MenuItem, Divider } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api/api';
import logo from '../assets/images/logo.png';
import NotificationsBell from '../components/NotificationsBell';
import { serviciosVisibles, rutaDeServicio } from '../utils/servicios';
import { iconoDeServicio } from '../utils/iconoServicio';

// ── Íconos SVG inline (reemplaza las imágenes de beneficios) ──────────────────
// Pin de ubicación (el mismo dibujo que IconPin de ClientRequestsPage). Antes
// era una persona con un tilde de verificado: decía con el dibujo lo que el
// texto ya no dice (FIXO no verifica, Términos 5.3). 29-10-2026.
const IconZona = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#163244" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3" stroke="#E7A432" strokeWidth="2.2"/>
  </svg>
);
const IconMobile = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#163244" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2"/>
    <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5"/>
    <path d="M9 7l2 2 4-4" stroke="#E7A432" strokeWidth="2.2"/>
  </svg>
);
// Burbuja de diálogo (el mismo contorno que el ícono de mensajes de
// ClientRequestsPage). Antes era un escudo con tilde: arriba de "Acordás con
// el prestador" seguía diciendo "seguro y verificado". 29-10-2026.
const IconAcuerdo = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#163244" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    <path d="M8 8h8M8 12h5" stroke="#E7A432" strokeWidth="2.2"/>
  </svg>
);
const Icon247 = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#163244" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
    <path d="M19.5 5.5l1.5 1.5-1.5 0.5" stroke="#E7A432" strokeWidth="1.8"/>
  </svg>
);

// ── Íconos de servicios SVG inline ────────────────────────────────────────────
const svcIconMap = {
  default: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E7A432" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
};

// ── Estilos globales inyectados una sola vez ──────────────────────────────────
const globalStyles = `

  .fixo-lp * { box-sizing: border-box; }

  /* Fondo hero — idéntico a fixo.com.ar */
  .fixo-hero-bg {
    position: absolute; inset: 0; z-index: 0; pointer-events: none;
    background-image:
      radial-gradient(circle at 15% 25%, rgba(231,164,50,0.08) 0%, transparent 50%),
      radial-gradient(circle at 85% 85%, rgba(30,74,110,0.65) 0%, transparent 50%),
      repeating-linear-gradient(
        45deg,
        transparent, transparent 60px,
        rgba(255,255,255,0.015) 60px,
        rgba(255,255,255,0.015) 61px
      );
  }

  /* Círculos decorativos giratorios */
  .fixo-circle-1 {
    position: absolute; width: 600px; height: 600px; border-radius: 50%;
    border: 1px solid rgba(231,164,50,0.1);
    top: -220px; right: -160px; z-index: 0;
    animation: fixoSpin 30s linear infinite;
  }
  .fixo-circle-2 {
    position: absolute; width: 380px; height: 380px; border-radius: 50%;
    border: 1px solid rgba(231,164,50,0.06);
    top: -100px; right: -60px; z-index: 0;
    animation: fixoSpin 20s linear infinite reverse;
  }
  @keyframes fixoSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  /* Fade-in animaciones */
  @keyframes fixoFadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fixo-fade-1 { animation: fixoFadeUp 0.6s ease both; }
  .fixo-fade-2 { animation: fixoFadeUp 0.6s 0.1s ease both; }
  .fixo-fade-3 { animation: fixoFadeUp 0.6s 0.2s ease both; }
  .fixo-fade-4 { animation: fixoFadeUp 0.6s 0.3s ease both; }

  /* Hover en cards de servicio — fondo amarillo igual que index.html */
  .fixo-svc-chip:hover {
    background-color: rgba(231,164,50,0.12) !important;
    border-color: rgba(231,164,50,0.45) !important;
    transform: translateY(-2px);
  }
  .fixo-svc-chip { transition: background-color 0.15s, border-color 0.15s, transform 0.15s; }

  /* Hover en cards de beneficio */
  .fixo-why-card:hover {
    border-color: rgba(231,164,50,0.45) !important;
    transform: translateY(-3px);
  }
  .fixo-why-card { transition: border-color 0.15s, transform 0.15s; }
  .fixo-why-card:hover .fixo-why-icon {
    background: rgba(231,164,50,0.15) !important;
  }

  /* Input focus */
  .fixo-input:focus {
    outline: none;
    border-color: rgba(231,164,50,0.55) !important;
  }

  /* Nav links hover */
  .fixo-nav-link:hover { color: #E7A432 !important; }

  /* Botón outline hover */
  .fixo-btn-outline:hover {
    background: rgba(255,255,255,0.08) !important;
  }

  /* Steps fondo — igual que el hero */
  .fixo-steps-bg {
    position: absolute; inset: 0; z-index: 0; pointer-events: none;
    background-image:
      radial-gradient(circle at 85% 15%, rgba(231,164,50,0.07) 0%, transparent 50%),
      radial-gradient(circle at 15% 85%, rgba(30,74,110,0.5) 0%, transparent 50%),
      repeating-linear-gradient(
        45deg,
        transparent, transparent 60px,
        rgba(255,255,255,0.015) 60px,
        rgba(255,255,255,0.015) 61px
      );
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Scroll automático si viene con ?scroll=servicios desde otra página
  useEffect(() => {
    const target = searchParams.get('scroll');
    if (!target) return;
    const delay = setTimeout(() => {
      const refMap = {
        servicios:    serviciosRef,
        porque:       porQueRef,
        comofunciona: comoFuncionaRef,
      };
      refMap[target]?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300); // pequeño delay para que el DOM esté listo
    return () => clearTimeout(delay);
  }, [location.search]);
  const { login, user } = useContext(AuthContext);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/');
  };

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loginErr, setLoginErr] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const { login: authLogin, logout } = useContext(AuthContext);

  // ── Links en navbar según rol ─────────────────────────────────────────────
  // Admin ve SOLO sus links (sin Servicios/¿Por qué Fixo?/Cómo funciona)
  // Otros roles ven los públicos + los suyos
  const adminNavLinks = [
    { label: 'Dashboard',   to: '/admin/dashboard' },
    { label: 'Usuarios',    to: '/admin/users' },
    { label: 'Solicitudes', to: '/admin/requests' },
    { label: 'Prestadores', to: '/admin/prestadores' },
    { label: 'Formularios', to: '/admin/formularios' },
    { label: 'Mi perfil',   to: '/perfil' },
  ];

  const extraNavLinks = user ? {
    user: [
      { label: 'Mis solicitudes', to: '/mis-solicitudes' },
      { label: 'Mi perfil',       to: '/perfil' },
    ],
    prestador: [
      { label: 'Solicitudes',  to: '/prestador/solicitudes' },
      { label: 'Presupuestos', to: '/prestador/presupuestos' },
      { label: 'Agenda',       to: '/prestador/agenda' },
      { label: 'Mi perfil',    to: '/prestador/perfil' },
    ],
    admin: adminNavLinks,
  }[user.role] || [] : [];

  const serviciosRef    = useRef(null);
  const beneficiosRef   = useRef(null);
  const porQueRef       = useRef(null);
  const comoFuncionaRef = useRef(null);

  // Inyectar estilos globales una sola vez
  useEffect(() => {
    if (!document.getElementById('fixo-lp-styles')) {
      const style = document.createElement('style');
      style.id = 'fixo-lp-styles';
      style.innerHTML = globalStyles;
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById('fixo-lp-styles');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    api.get('/services')
      .then(res => {
        const sorted = res.data.sort((a, b) => a.title.localeCompare(b.title));
        setServices(sorted);
      })
      .catch(err => console.error('Error al cargar servicios', err))
      .finally(() => setLoading(false));
  }, []);

  const handleLogin = async () => {
    setLoginErr('');
    try {
      await login(email, password);
      // La redirección la maneja App.js según el rol
    } catch {
      setLoginErr('Email o contraseña incorrectos');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  const scrollTo = (ref) => ref?.current?.scrollIntoView({ behavior: 'smooth' });

  // Desde otra página: navega a la landing con parámetro de scroll
  const smartScrollTo = (ref, key) => {
    if (location.pathname === '/') {
      scrollTo(ref);
    } else {
      navigate(`/?scroll=${key}`);
    }
  };

  // ── Datos de beneficios ────────────────────────────────────────────────────
  // Textos de Diego (29-10-2026). Cada uno tiene que ser cierto: FIXO no
  // verifica a los prestadores (Términos 5.3); el pago se arregla fuera de la
  // plataforma (Términos 6.1 y 6.2: lo de pagos no está operativo), y pedir
  // presupuestos es gratis (el cargo por cancelación de payments.js solo
  // corre con un pago hecho por la plataforma); y no hay prestadores de
  // guardia: "te responden cuando la ven" evita que alguien con una urgencia
  // de noche entienda que le mandan a alguien.
  const beneficios = [
    { icon: <IconZona />,       title: 'Profesionales en tu zona',   desc: 'Te contactan prestadores que trabajan en tu barrio o tu ciudad.' },
    { icon: <IconMobile />,     title: 'Solicitá desde el celular',  desc: 'Usá Fixo desde cualquier dispositivo. Rápido, simple y sin instalaciones.' },
    { icon: <IconAcuerdo />,    title: 'Acordás con el prestador',   desc: 'Pedir presupuestos no te cuesta nada. El precio y la forma de pago los arreglás directamente con quien hace el trabajo.' },
    { icon: <Icon247 />,        title: 'Pedí a cualquier hora',      desc: 'Publicá tu solicitud de día o de noche. Los prestadores te responden cuando la ven.' },
  ];

  // ── Config hero según rol ──────────────────────────────────────────────────
  const heroConfig = {
    user: {
      roleLabel: 'Cliente',
      btnPrimary:   { label: 'Solicitar un servicio', to: '/services' },
      btnSecondary: { label: 'Mis solicitudes',        to: '/mis-solicitudes' },
      quickActions: [
        { label: 'Nueva solicitud',  sub: 'Pedí un servicio ahora',    to: '/services',        icon: 'plus' },
        { label: 'Mis solicitudes',  sub: 'Ver estado de tus pedidos', to: '/mis-solicitudes', icon: 'check' },
        { label: 'Mi perfil',        sub: 'Editá tu información',      to: '/perfil',          icon: 'user' },
      ],
    },
    prestador: {
      roleLabel: 'Prestador',
      btnPrimary:   { label: 'Ver solicitudes', to: '/prestador/solicitudes' },
      btnSecondary: { label: 'Mi agenda',        to: '/prestador/agenda' },
      quickActions: [
        { label: 'Solicitudes nuevas', sub: 'Revisá los pedidos pendientes', to: '/prestador/solicitudes', icon: 'doc' },
        { label: 'Mi agenda',          sub: 'Tus trabajos programados',      to: '/prestador/agenda',      icon: 'cal' },
        { label: 'Mi perfil',          sub: 'Editá tu información',          to: '/prestador/perfil',      icon: 'user' },
      ],
    },
    admin: {
      roleLabel: 'Administrador',
      btnPrimary:   { label: 'Dashboard',          to: '/admin/dashboard' },
      btnSecondary: { label: 'Gestionar usuarios',  to: '/admin/users' },
      quickActions: [
        { label: 'Dashboard',   sub: 'Métricas en tiempo real',    to: '/admin/dashboard',   icon: 'chart' },
        { label: 'Usuarios',    sub: 'Gestionar cuentas',           to: '/admin/users',       icon: 'users' },
        { label: 'Solicitudes', sub: 'Ver y asignar pedidos',       to: '/admin/requests',    icon: 'doc' },
        { label: 'Prestadores', sub: 'Gestionar prestadores',       to: '/admin/prestadores', icon: 'users' },
        { label: 'Formularios', sub: 'Editar campos de servicios',  to: '/admin/formularios', icon: 'form' },
      ],
    },
  };

  const roleData = user ? heroConfig[user.role] : null;

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '';

  // ── Íconos para acciones rápidas ──────────────────────────────────────────
  const QaIcon = ({ type }) => {
    const icons = {
      plus:  <path d="M12 5v14M5 12h14" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/>,
      check: <><path d="M9 11l3 3L22 4" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/></>,
      card:  <><rect x="2" y="5" width="20" height="14" rx="2" stroke="#E7A432" strokeWidth="2"/><line x1="2" y1="10" x2="22" y2="10" stroke="#E7A432" strokeWidth="2"/></>,
      doc:   <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/><polyline points="14 2 14 8 20 8" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/></>,
      cal:   <><rect x="3" y="4" width="18" height="18" rx="2" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/><line x1="16" y1="2" x2="16" y2="6" stroke="#E7A432" strokeWidth="2"/><line x1="8" y1="2" x2="8" y2="6" stroke="#E7A432" strokeWidth="2"/><line x1="3" y1="10" x2="21" y2="10" stroke="#E7A432" strokeWidth="2"/></>,
      user:  <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="7" r="4" stroke="#E7A432" strokeWidth="2"/></>,
      users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="#E7A432" strokeWidth="2"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/></>,
      form:  <><rect x="3" y="3" width="18" height="18" rx="2" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/><path d="M9 9h6M9 12h6M9 15h4" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"/></>,
      chart: <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke="#E7A432" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></>,
    };
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        {icons[type]}
      </svg>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box className="fixo-lp" sx={{ fontFamily: "'Montserrat', sans-serif", minHeight: '100vh', bgcolor: '#f5f7fa', color: '#163244' }}>

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <Box component="nav" sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
        bgcolor: '#163244',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 2, md: 3 }, height: 60,
      }}>

        {/* Izquierda: hamburger (mobile) + logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Hamburger solo en mobile */}
          <Box
            component="button"
            onClick={() => setDrawerOpen(true)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center', justifyContent: 'center',
              bgcolor: 'transparent', border: 'none', cursor: 'pointer',
              color: '#fff', p: 0.5, borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </Box>

          {/* Logo */}
          <Box
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              transition: 'transform 0.15s, opacity 0.15s',
              '&:hover': { transform: 'scale(0.96)', opacity: 0.85 },
              '&:active': { transform: 'scale(0.92)' },
            }}
          >
            <Box component="img" src={logo} alt="Fixo — volver al inicio" sx={{ height: 32, objectFit: 'contain' }} />
          </Box>
        </Box>

        {/* Centro: links desktop */}
        <Box sx={{
          display: { xs: 'none', md: 'flex' },
          gap: 2, alignItems: 'center',
          flexWrap: 'nowrap', overflow: 'hidden',
        }}>
          {/* Links públicos — solo si NO es admin */}
          {user?.role !== 'admin' && [
            { label: 'Servicios',      action: () => smartScrollTo(serviciosRef,    'servicios') },
            { label: '¿Por qué Fixo?', action: () => smartScrollTo(porQueRef,       'porque') },
            { label: 'Cómo funciona',  action: () => smartScrollTo(comoFuncionaRef, 'comofunciona') },
          ].map(({ label, action }) => (
            <Typography key={label} onClick={action} className="fixo-nav-link" sx={{
              fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)',
              cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
              transition: 'color 0.15s', whiteSpace: 'nowrap',
            }}>{label}</Typography>
          ))}

          {/* Links según rol (logueado) */}
          {user && extraNavLinks.map(({ label, to }) => (
            <Typography key={to} onClick={() => navigate(to)} className="fixo-nav-link" sx={{
              fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)',
              cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
              transition: 'color 0.15s', whiteSpace: 'nowrap',
            }}>{label}</Typography>
          ))}
        </Box>

        {/* Derecha: avatar si logueado, botón registrarse si no */}
        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{
              color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat', sans-serif",
              fontWeight: 500, fontSize: 12, display: { xs: 'none', sm: 'block' },
            }}>
              {user.name}
            </Typography>
            <NotificationsBell />
            <Box onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ cursor: 'pointer' }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#E7A432', color: '#163244', fontSize: 13, fontWeight: 700 }}>
                {initials}
              </Avatar>
            </Box>
          </Box>
        ) : (
          <Box
            component="button"
            onClick={() => navigate('/registro')}
            sx={{
              bgcolor: '#E7A432', color: '#163244', border: 'none',
              borderRadius: '50px', px: 2.5, py: 0.9,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Montserrat', sans-serif",
              '&:hover': { bgcolor: '#f5bc55' }, transition: 'background 0.15s',
            }}
          >Registrarse</Box>
        )}
      </Box>

      {/* ── DRAWER MOBILE ───────────────────────────────────────────────────── */}
      {drawerOpen && (
        <>
          {/* Overlay */}
          <Box
            onClick={() => setDrawerOpen(false)}
            sx={{
              position: 'fixed', inset: 0, zIndex: 1300,
              bgcolor: 'rgba(0,0,0,0.4)',
            }}
          />
          {/* Panel */}
          <Box sx={{
            position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 1400,
            width: 260, bgcolor: '#fff',
            borderRadius: '0 16px 16px 0',
            display: 'flex', flexDirection: 'column',
            boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
          }}>
            {/* Header del drawer */}
            <Box sx={{ bgcolor: '#163244', px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {user ? (
                <>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#E7A432', color: '#163244', fontSize: 13, fontWeight: 700 }}>
                    {initials}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat', sans-serif" }}>
                      {user.name}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat', sans-serif" }}>
                      {roleData?.roleLabel}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box component="img" src={logo} alt="Fixo" sx={{ height: 28, objectFit: 'contain' }} />
              )}
              {/* Botón cerrar */}
              <Box
                component="button"
                onClick={() => setDrawerOpen(false)}
                sx={{ ml: 'auto', bgcolor: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', p: 0.5 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </Box>
            </Box>

            {/* Links del drawer */}
            <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
              {/* Públicos */}
              <Typography sx={{ px: 2.5, py: 1, fontSize: 10, fontWeight: 700, color: '#aab8c2', textTransform: 'uppercase', letterSpacing: 1, fontFamily: "'Montserrat', sans-serif" }}>
                Explorar
              </Typography>
              {[
                { label: 'Servicios',      action: () => { smartScrollTo(serviciosRef,    'servicios');    setDrawerOpen(false); } },
                { label: '¿Por qué Fixo?', action: () => { smartScrollTo(porQueRef,       'porque');       setDrawerOpen(false); } },
                { label: 'Cómo funciona',  action: () => { smartScrollTo(comoFuncionaRef, 'comofunciona'); setDrawerOpen(false); } },
              ].map(({ label, action }) => (
                <Box
                  key={label}
                  component="button"
                  onClick={action}
                  sx={{
                    width: '100%', bgcolor: 'transparent', border: 'none',
                    px: 2.5, py: 1.2, textAlign: 'left', cursor: 'pointer',
                    fontFamily: "'Montserrat', sans-serif", fontSize: 13,
                    fontWeight: 500, color: '#163244',
                    '&:hover': { bgcolor: 'rgba(231,164,50,0.08)' },
                    display: 'block',
                  }}
                >{label}</Box>
              ))}

              {/* Links extra logueado */}
              {user && extraNavLinks.length > 0 && (
                <>
                  <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.07)', my: 1 }} />
                  <Typography sx={{ px: 2.5, py: 1, fontSize: 10, fontWeight: 700, color: '#aab8c2', textTransform: 'uppercase', letterSpacing: 1, fontFamily: "'Montserrat', sans-serif" }}>
                    Mi cuenta
                  </Typography>
                  {extraNavLinks.map(({ label, to }) => (
                    <Box
                      key={to}
                      component="button"
                      onClick={() => { navigate(to); setDrawerOpen(false); }}
                      sx={{
                        width: '100%', bgcolor: 'transparent', border: 'none',
                        px: 2.5, py: 1.2, textAlign: 'left', cursor: 'pointer',
                        fontFamily: "'Montserrat', sans-serif", fontSize: 13,
                        fontWeight: 500, color: '#163244',
                        '&:hover': { bgcolor: 'rgba(231,164,50,0.08)' },
                        display: 'block',
                      }}
                    >{label}</Box>
                  ))}
                </>
              )}
            </Box>

            {/* Footer del drawer */}
            <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.07)', p: 2 }}>
              {user ? (
                <Box
                  component="button"
                  onClick={() => { handleLogout(); setDrawerOpen(false); }}
                  sx={{
                    width: '100%', bgcolor: 'transparent', border: '1px solid rgba(192,57,43,0.2)',
                    borderRadius: '8px', px: 2, py: 1.2, cursor: 'pointer',
                    fontFamily: "'Montserrat', sans-serif", fontSize: 13,
                    fontWeight: 600, color: '#c0392b',
                    '&:hover': { bgcolor: 'rgba(192,57,43,0.06)' },
                  }}
                >Cerrar sesión</Box>
              ) : (
                <Box
                  component="button"
                  onClick={() => { navigate('/registro'); setDrawerOpen(false); }}
                  sx={{
                    width: '100%', bgcolor: '#E7A432', border: 'none',
                    borderRadius: '8px', px: 2, py: 1.2, cursor: 'pointer',
                    fontFamily: "'Montserrat', sans-serif", fontSize: 13,
                    fontWeight: 700, color: '#163244',
                  }}
                >Registrarse</Box>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* ── MENÚ AVATAR (solo logueado) ─────────────────────────────────────── */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1, minWidth: 220, borderRadius: '14px',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ px: 2.5, py: 1.8, bgcolor: '#163244' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat', sans-serif" }}>
            {user?.name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat', sans-serif" }}>
            {user?.email}
          </Typography>
        </Box>
        <MenuItem
          onClick={() => { navigate('/perfil'); setAnchorEl(null); }}
          sx={{ gap: 1.5, py: 1.3, px: 2.5, '&:hover': { bgcolor: 'rgba(231,164,50,0.08)' } }}
        >
          <PersonIcon fontSize="small" sx={{ color: '#163244' }} />
          <Typography sx={{ fontSize: 13, fontFamily: "'Montserrat', sans-serif", color: '#163244' }}>Mi perfil</Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{ gap: 1.5, py: 1.3, px: 2.5, '&:hover': { bgcolor: 'rgba(192,57,43,0.06)' } }}
        >
          <LogoutIcon fontSize="small" sx={{ color: '#c0392b' }} />
          <Typography sx={{ fontSize: 13, fontFamily: "'Montserrat', sans-serif", color: '#c0392b' }}>Cerrar sesión</Typography>
        </MenuItem>
      </Menu>

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <Box sx={{
        position: 'relative', bgcolor: '#163244', overflow: 'hidden',
        pt: { xs: 14, md: 16 }, pb: { xs: 7, md: 9 },
        px: { xs: 3, md: 6 },
      }}>
        <div className="fixo-hero-bg" />
        <div className="fixo-circle-1" />
        <div className="fixo-circle-2" />

        <Box sx={{
          position: 'relative', zIndex: 1,
          maxWidth: 1000, margin: '0 auto',
          display: 'flex', gap: { xs: 0, md: 5 },
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
        }}>

          {/* Copy izquierda */}
          <Box sx={{ flex: 1 }} className="fixo-fade-1">
            <Box sx={{
              display: 'inline-block',
              bgcolor: 'rgba(231,164,50,0.1)', color: '#E7A432',
              border: '1px solid rgba(231,164,50,0.25)',
              borderRadius: '50px', px: 2, py: 0.5,
              fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
              textTransform: 'uppercase', mb: 2.5,
            }}>Plataforma de Servicios Profesionales</Box>

            <Typography sx={{
              fontSize: { xs: 28, md: 36 }, fontWeight: 900, lineHeight: 1.15,
              color: '#fff', mb: 2, fontFamily: "'Montserrat', sans-serif",
            }}>
              Tu hogar en las{' '}
              <Box component="span" sx={{ color: '#E7A432' }}>mejores manos</Box>
            </Typography>

            <Typography sx={{
              fontSize: 13, color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.75, mb: 3.5, maxWidth: 400,
              fontFamily: "'Montserrat', sans-serif",
            }}>
              Conectamos clientes con prestadores para resolver cualquier necesidad: hogares, empresas e industrias. Rápido, seguro y confiable.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Box
                component="button"
                onClick={() => navigate(user ? roleData.btnPrimary.to : '/registro')}
                sx={{
                  bgcolor: '#E7A432', color: '#163244', border: 'none',
                  borderRadius: '50px', px: 3, py: 1.2,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif",
                  '&:hover': { bgcolor: '#f5bc55' }, transition: 'background 0.15s',
                }}
              >{user ? roleData.btnPrimary.label : 'Solicitar un servicio'}</Box>

              {/* Sin sesión no hay botón: decía "Soy prestador" y llevaba a /registro,
                  que crea un cliente. El alta de prestador no existe todavía
                  (RONDA-SIGUIENTE-ALTA-DE-PRESTADOR-NO-EXISTE). */}
              {user && <Box
                component="button"
                className="fixo-btn-outline"
                onClick={() => navigate(roleData.btnSecondary.to)}
                sx={{
                  bgcolor: 'transparent', color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.2)',
                  borderRadius: '50px', px: 3, py: 1.1,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif", transition: 'background 0.15s',
                }}
              >{roleData.btnSecondary.label}</Box>}
            </Box>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 4, mt: 4 }}>
              {[
                { n: '27',     l: 'Categorías' },
              ].map(({ n, l }) => (
                <Box key={l}>
                  <Typography sx={{ fontSize: 20, fontWeight: 900, color: '#E7A432', fontFamily: "'Montserrat', sans-serif" }}>{n}</Typography>
                  <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', mt: 0.3, fontFamily: "'Montserrat', sans-serif" }}>{l}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Card derecha: bienvenida si logueado, login si no */}
          <Box className="fixo-fade-2" sx={{
            width: { xs: '100%', md: 290 }, flexShrink: 0,
            mt: { xs: 5, md: 0 },
          }}>
            <Box sx={{
              bgcolor: 'rgba(10,22,32,0.72)', backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px', p: 3.5,
            }}>

              {user ? (
                /* ── WELCOME CARD ── */
                <>
                  {/* Avatar con iniciales */}
                  <Box sx={{
                    width: 52, height: 52, borderRadius: '50%',
                    bgcolor: '#E7A432', color: '#163244',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 900, mb: 1.8,
                    fontFamily: "'Montserrat', sans-serif",
                  }}>{initials}</Box>

                  <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: 500, mb: 0.4, fontFamily: "'Montserrat', sans-serif" }}>
                    Bienvenido de vuelta
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#fff', mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
                    {user.name}
                  </Typography>
                  <Box sx={{
                    display: 'inline-block',
                    bgcolor: 'rgba(231,164,50,0.15)', color: '#E7A432',
                    border: '1px solid rgba(231,164,50,0.3)',
                    borderRadius: '50px', px: 1.5, py: 0.4,
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
                    textTransform: 'uppercase', mb: 2.5,
                    fontFamily: "'Montserrat', sans-serif",
                  }}>{roleData?.roleLabel}</Box>

                  <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', mb: 2 }} />

                  {/* Acciones rápidas — 2 col para admin, 1 col para otros roles */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: user?.role === 'admin' ? '1fr 1fr' : '1fr', gap: 1 }}>
                    {roleData?.quickActions.map(({ label, sub, to, icon }) => (
                      <Box
                        key={to}
                        component="button"
                        onClick={() => navigate(to)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: '10px',
                          p: user?.role === 'admin' ? '8px 10px' : '10px 14px',
                          display: 'flex', alignItems: 'center', gap: 1,
                          cursor: 'pointer', textAlign: 'left',
                          fontFamily: "'Montserrat', sans-serif",
                          transition: 'all 0.15s',
                          '&:hover': {
                            bgcolor: 'rgba(231,164,50,0.1)',
                            borderColor: 'rgba(231,164,50,0.3)',
                          },
                        }}
                      >
                        <Box sx={{
                          width: user?.role === 'admin' ? 24 : 28,
                          height: user?.role === 'admin' ? 24 : 28,
                          borderRadius: '7px',
                          bgcolor: 'rgba(231,164,50,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <QaIcon type={icon} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: user?.role === 'admin' ? 11 : 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)', fontFamily: "'Montserrat', sans-serif", lineHeight: 1.2 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontSize: user?.role === 'admin' ? 9 : 10, color: 'rgba(255,255,255,0.35)', mt: 0.2, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.2 }}>
                            {sub}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                /* ── LOGIN CARD ── */
                <>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff', mb: 0.5, fontFamily: "'Montserrat', sans-serif" }}>
                    Iniciar sesión
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', mb: 2.5, fontFamily: "'Montserrat', sans-serif" }}>
                    Accedé a tu cuenta Fixo
                  </Typography>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.6, fontFamily: "'Montserrat', sans-serif" }}>
                      Email
                    </Typography>
                    <input
                      className="fixo-input"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={{
                        width: '100%', background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9,
                        padding: '9px 12px', color: '#fff', fontSize: 13,
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.6, fontFamily: "'Montserrat', sans-serif" }}>
                      Contraseña
                    </Typography>
                    <input
                      className="fixo-input"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={{
                        width: '100%', background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9,
                        padding: '9px 12px', color: '#fff', fontSize: 13,
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    />
                  </Box>

                  {loginErr && (
                    <Typography sx={{ fontSize: 11, color: '#f87171', mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
                      {loginErr}
                    </Typography>
                  )}

                  {/* Ahora SÍ es un link, y tiene flujo detrás: /recuperar manda un
                      código de 6 dígitos al mail de la cuenta.
                      Historia de esta línea, porque vale como recordatorio: vivió
                      cuatro meses con cursor:pointer y color de acento pero sin
                      onClick —parecía link y no hacía nada—, después pasó a texto
                      plano honesto mientras no existía el flujo, y recién ahora
                      vuelve a ser link. Ver DEUDA-FRONTEND-LINK-MUERTO-OLVIDASTE-
                      CONTRASENA y DEUDA-RECUPERACION-PASSWORD-NO-EXISTE. */}
                  <Typography
                    component={RouterLink}
                    to="/recuperar"
                    sx={{
                      display: 'block', textAlign: 'right', fontSize: 10,
                      color: '#E7A432', mb: 2, cursor: 'pointer',
                      textDecoration: 'none',
                      fontFamily: "'Montserrat', sans-serif",
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </Typography>

                  <Box
                    component="button"
                    onClick={handleLogin}
                    sx={{
                      width: '100%', bgcolor: '#E7A432', color: '#163244', border: 'none',
                      borderRadius: '50px', py: 1.3, fontSize: 13, fontWeight: 700,
                      cursor: 'pointer', mb: 1.5, fontFamily: "'Montserrat', sans-serif",
                      '&:hover': { bgcolor: '#f5bc55' }, transition: 'background 0.15s',
                    }}
                  >Ingresar</Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }} />
                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: "'Montserrat', sans-serif" }}>o</Typography>
                    <Box sx={{ flex: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }} />
                  </Box>

                  <Typography sx={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: "'Montserrat', sans-serif" }}>
                    ¿No tenés cuenta?{' '}
                    <Box component="span" onClick={() => navigate('/registro')} sx={{ color: '#E7A432', fontWeight: 700, cursor: 'pointer' }}>
                      Registrate gratis
                    </Box>
                  </Typography>
                </>
              )}

            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── SERVICIOS ──────────────────────────────────────────────────────── */}
      <Box ref={serviciosRef} sx={{ bgcolor: '#f5f7fa', px: { xs: 3, md: 6 }, py: 7 }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#E7A432', letterSpacing: 2, textTransform: 'uppercase', mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
            Categorías
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#163244', mb: 0.8, fontFamily: "'Montserrat', sans-serif" }}>
            ¿Qué necesitás resolver hoy?
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#7a93a4', mb: 3.5, fontFamily: "'Montserrat', sans-serif" }}>
            {/* ⬜ "categorías" o "servicios": son 27 servicios en 7 categorías.
                Queda el texto viejo hasta que Diego decida (03-11-2026). */}
            {services.length} categorías disponibles en toda la plataforma
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#E7A432' }} />
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: 2,
            }}>
              {/* El slug y el ícono salen de la base; sin slug el servicio no
                  se muestra. Deducir el slug del título mandaba a un
                  formulario que no existe (03-11-2026). */}
              {serviciosVisibles(services, false).map((svc, i) => (
                <Box
                  key={i}
                  className="fixo-svc-chip"
                  onClick={() => {
                    if (user) {
                      navigate(rutaDeServicio(svc), { state: { serviceTitle: svc.title } });
                    } else {
                      // No logueado: scroll al login card en el hero
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  sx={{
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.07)',
                    borderRadius: '14px',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    minHeight: 110,
                    cursor: 'pointer',
                    gap: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={iconoDeServicio(svc.icon)}
                    alt={svc.title}
                    sx={{ width: 48, height: 48, objectFit: 'contain' }}
                  />
                  <Typography sx={{
                    fontSize: 11, fontWeight: 600, color: '#2e4a5c',
                    fontFamily: "'Montserrat', sans-serif",
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                  }}>
                    {svc.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── ¿POR QUÉ FIXO? ─────────────────────────────────────────────────── */}
      <Box ref={porQueRef} sx={{ bgcolor: '#fff', px: { xs: 3, md: 6 }, py: 7 }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#E7A432', letterSpacing: 2, textTransform: 'uppercase', mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
            Beneficios
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#163244', mb: 0.8, fontFamily: "'Montserrat', sans-serif" }}>
            ¿Por qué elegir Fixo?
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#7a93a4', mb: 4, fontFamily: "'Montserrat', sans-serif" }}>
            Todo lo que necesitás para resolver tu hogar sin complicaciones
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2.5,
          }}>
            {beneficios.map((b, i) => (
              <Box
                key={i}
                className="fixo-why-card"
                sx={{
                  bgcolor: '#f5f7fa', border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: '18px', p: 3.5, textAlign: 'center',
                }}
              >
                <Box
                  className="fixo-why-icon"
                  sx={{
                    width: 62, height: 62, borderRadius: '16px',
                    bgcolor: 'rgba(22,50,68,0.07)',
                    margin: '0 auto', mb: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.15s',
                  }}
                >
                  {b.icon}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#163244', mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
                  {b.title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#7a93a4', lineHeight: 1.65, fontFamily: "'Montserrat', sans-serif" }}>
                  {b.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── CÓMO FUNCIONA ──────────────────────────────────────────────────── */}
      <Box ref={comoFuncionaRef} sx={{ position: 'relative', bgcolor: '#163244', overflow: 'hidden', px: { xs: 3, md: 6 }, py: 7 }}>
        <div className="fixo-steps-bg" />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1000, margin: '0 auto' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#E7A432', letterSpacing: 2, textTransform: 'uppercase', mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
            Proceso
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mb: 4, fontFamily: "'Montserrat', sans-serif" }}>
            Cómo funciona Fixo
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}>
            {[
              { n: '01', t: 'Publicá tu solicitud',  d: 'Describí el servicio que necesitás con urgencia y horario preferido.' },
              { n: '02', t: 'Recibí presupuestos',   d: 'Los prestadores te contactan con propuestas para comparar.' },
              { n: '03', t: 'Coordiná y resolvé',    d: 'Confirmá el trabajo, hacé seguimiento y calificá al prestador.' },
            ].map(({ n, t, d }) => (
              <Box key={n} sx={{
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px', p: 3,
              }}>
                <Typography sx={{ fontSize: 40, fontWeight: 900, color: 'rgba(231,164,50,0.18)', lineHeight: 1, mb: 1.5, fontFamily: "'Montserrat', sans-serif" }}>
                  {n}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', mb: 0.8, fontFamily: "'Montserrat', sans-serif" }}>
                  {t}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.65, fontFamily: "'Montserrat', sans-serif" }}>
                  {d}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <Box component="footer" sx={{
        bgcolor: '#0f2233',
        px: { xs: 3, md: 6 }, py: 2.5,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 1.5,
      }}>
        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: "'Montserrat', sans-serif" }}>
          © 2026 Fixo — Todos los derechos reservados
        </Typography>
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
          {[
            { label: 'Términos y Condiciones', to: '/terminos' },
            { label: 'Privacidad',             to: '/privacidad' },
            { label: 'Cookies',                to: '/cookies' },
            { label: 'Accesibilidad',          to: '/accesibilidad' },
            { label: 'Comunidad',              to: '/comunidad' },
          ].map(({ label, to }) => (
            <RouterLink
              key={to}
              to={to}
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.3)',
                fontFamily: "'Montserrat', sans-serif",
                textDecoration: 'none',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.target.style.color = '#E7A432'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.3)'}
            >
              {label}
            </RouterLink>
          ))}
        </Box>
      </Box>

    </Box>
  );
};

export default LandingPage;
